import { captureException } from '@sentry/react';
import { PartnerOrganization } from 'khshared/API';
import { getBrandConfigByBrandCode } from 'khshared/metadata/getBrandConfig';
import getPartnerConfig from 'khshared/metadata/getPartnerConfig';
import React, { useContext } from 'react';
import { Image, ImageStyle, Text, TextStyle, ViewStyle, useWindowDimensions } from 'react-native';

import logo from '../assets/logo_32h.png';
import logoV2 from '../assets/logo_56h_v2.png';
import { KHColors, KHThemeContext } from '../khui';
import generateURI from '../xapis/generateURIXAPI';

const logoURI = generateURI(logo);
const logoV2URI = generateURI(logoV2);

const styles = {
  logoArea: {
    flex: 1,
    flexDirection: 'row',
    position: 'absolute',
    left: '50%',
    right: '50%',
    height: 28,
    justifyContent: 'center',
    paddingHorizontal: 0,
  } as ViewStyle,
  logo: {
    width: 112,
    height: 28,
    resizeMode: 'contain',
  } as ImageStyle,
  logoPlus: {
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
    marginLeft: 8,
    marginRight: 8,
    color: KHColors.logoPlus,
  } as TextStyle,
};

interface Props {
  partnerOrganization?: PartnerOrganization | null;
  orientation?: 'partner-first';
  brandCode?: string | null | undefined;
}

export default function AppBarLogo({
  partnerOrganization,
  orientation,
  brandCode,
}: Props): JSX.Element {
  const context = useContext(KHThemeContext);

  const { width } = useWindowDimensions();

  const shouldShrinkBanners = width < 350;

  const sprinterLogoStyle = {
    ...styles.logo,
    ...(shouldShrinkBanners ? { width: 75, height: 18.7 } : { width: 112, height: 28 }),
  };

  const sprinterLogo = (
    <Image source={{ uri: context === 'v2' ? logoV2URI : logoURI }} style={sprinterLogoStyle} />
  );

  const brandConfig = getBrandConfigByBrandCode(brandCode);

  const brandSpecificCobrandingLogo = brandConfig?.cobrandingLogo;

  if (brandCode && !brandConfig) {
    captureException(
      new Error(`Brand code ${brandCode} not found in brand config but seen in AppBar.`),
    );
  }

  if (brandSpecificCobrandingLogo) {
    // These banners are exported at 2x so we need to divide by 2 to get the actual size
    const brandSpecificCobrandingLogoWidth = shouldShrinkBanners
      ? brandSpecificCobrandingLogo.width / 1.5
      : brandSpecificCobrandingLogo.width / 1;
    const brandSpecificCobrandingLogoHeight = shouldShrinkBanners
      ? brandSpecificCobrandingLogo.height / 1.5
      : brandSpecificCobrandingLogo.height / 1;
    // Cobranded brand logos all include the sprinter logo, so we don't need to include it here. This was discussed as a new and safer pattern for future logos.
    return (
      <>
        <Image
          source={brandSpecificCobrandingLogo}
          style={{
            ...styles.logo,
            width: brandSpecificCobrandingLogoWidth,
            height: brandSpecificCobrandingLogoHeight,
          }}
        />
      </>
    );
  }

  if (partnerOrganization == null) return sprinterLogo;

  const partnerConfig = getPartnerConfig(partnerOrganization);

  const cobrandingLogo = 'cobrandingLogo' in partnerConfig ? partnerConfig.cobrandingLogo : null;

  if (cobrandingLogo == null) return sprinterLogo;

  const cobrandingLogoWidth = shouldShrinkBanners
    ? cobrandingLogo.width / 1.5
    : cobrandingLogo.width;
  const cobrandingLogoHeight = shouldShrinkBanners
    ? cobrandingLogo.height / 1.5
    : cobrandingLogo.height;

  if (orientation === 'partner-first') {
    return (
      <>
        <Image
          source={cobrandingLogo}
          style={{ ...styles.logo, width: cobrandingLogoWidth, height: cobrandingLogoHeight }}
        />
        <Text style={styles.logoPlus}>+</Text>
        {sprinterLogo}
      </>
    );
  }

  return (
    <>
      {sprinterLogo}
      <Text style={styles.logoPlus}>+</Text>
      <Image
        source={cobrandingLogo}
        style={{ ...styles.logo, width: cobrandingLogoWidth, height: cobrandingLogoHeight }}
      />
    </>
  );
}

AppBarLogo.titleStyle = styles.logoArea;
